import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Users, 
  FolderKanban, 
  ClipboardList, 
  CalendarDays, 
  DollarSign,
  LogOut,
  Menu,
  X,
  Globe,
  Clock,
  Settings,
  ListTodo,
  HelpCircle
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t, language, setLanguage } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/', icon: LayoutDashboard, label: t('dashboard') },
    { path: '/clients', icon: Users, label: t('clients') },
    { path: '/projects', icon: FolderKanban, label: t('projects') },
    { path: '/tasks', icon: ListTodo, label: t('tasks') },
    { path: '/intakes', icon: ClipboardList, label: t('intakes') },
    { path: '/calendar', icon: CalendarDays, label: t('calendar') },
    { path: '/time-expenses', icon: Clock, label: t('timeExpenses') },
    { path: '/finances', icon: DollarSign, label: t('finances') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#E7E5E4] z-40 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Organizer Space" className="h-8 w-8 object-contain" />
          <span className="font-medium text-[#292524]">{t('appName')}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Language Switcher Mobile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9" data-testid="mobile-lang-btn">
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage('en')} className={language === 'en' ? 'bg-[#F5F5F4]' : ''}>
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('nl')} className={language === 'nl' ? 'bg-[#F5F5F4]' : ''}>
                Nederlands
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-menu-btn"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 h-full w-64 bg-[#F5F5F4] border-r border-[#E7E5E4] z-40 transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="p-6 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-14 h-14 flex items-center justify-center">
                <img src="/logo.png" alt="Organizer Space" className="h-12 w-12 object-contain" />
              </div>
              <h1 className="font-medium text-[#292524] text-lg">{t('appName')}</h1>
            </div>
          </div>

          {/* Language Switcher Desktop */}
          <div className="mb-6 flex items-center gap-1 bg-[#E7E5E4]/50 rounded-full p-1">
            <Button
              variant={language === 'en' ? 'default' : 'ghost'}
              size="sm"
              className={`flex-1 rounded-full h-8 text-xs ${language === 'en' ? 'bg-[#57534E] text-white' : 'text-[#78716C]'}`}
              onClick={() => setLanguage('en')}
              data-testid="sidebar-lang-en-btn"
            >
              EN
            </Button>
            <Button
              variant={language === 'nl' ? 'default' : 'ghost'}
              size="sm"
              className={`flex-1 rounded-full h-8 text-xs ${language === 'nl' ? 'bg-[#57534E] text-white' : 'text-[#78716C]'}`}
              onClick={() => setLanguage('nl')}
              data-testid="sidebar-lang-nl-btn"
            >
              NL
            </Button>
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#E7E5E4] text-[#292524]' 
                      : 'text-[#78716C] hover:bg-[#E7E5E4]/50 hover:text-[#292524]'
                  }`}
                  data-testid={`nav-${item.path === '/' ? 'dashboard' : item.path.slice(1)}`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium text-sm">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User section */}
        <div className="p-6 border-t border-[#E7E5E4] shrink-0">
          <Link
            to="/settings"
            onClick={() => setSidebarOpen(false)}
            className="flex items-center gap-3 mb-4 rounded-xl px-2 py-2 -mx-2 transition-all duration-200 hover:bg-[#E7E5E4]/50 cursor-pointer"
            data-testid="user-profile-settings-link"
          >
            <div className="w-10 h-10 rounded-full bg-[#78866B] flex items-center justify-center text-white font-medium shrink-0">
              {user?.name?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#292524] truncate text-sm">{user?.name}</p>
              <p className="text-xs text-[#78716C] truncate">{user?.email}</p>
            </div>
            <Settings className="w-4 h-4 text-[#A8A29E] shrink-0" />
          </Link>
          <Button
            variant="ghost"
            className="w-full justify-start text-[#78716C] hover:text-[#292524] hover:bg-[#E7E5E4]/50 mb-1"
            onClick={() => window.location.href = 'mailto:hello@organizerspace.com'}
            data-testid="report-issue-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            {t('reportAnIssue')}
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start text-[#78716C] hover:text-[#292524] hover:bg-[#E7E5E4]/50"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('signOut')}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className="lg:ml-64 pt-16 lg:pt-0 min-h-screen">
        <div className="p-4 sm:p-6 lg:p-8">
          {children}
        </div>
      </main>
    </div>
  );
}
